import { useEffect, useState, useRef } from 'react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAppAuth'
import { createLogger } from '@/utils/logger'

const logger = createLogger({ service: 'AuthCallbackPage' })

/**
 * Authentication callback page component that handles OAuth redirect from Cognito
 * Uses react-oidc-context for automatic callback processing and state management
 * 
 * Follows react-oidc-context patterns from fix-cognito-sign-in-react-samples.md
 */
export const AuthCallbackPage = (): JSX.Element => {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const hasProcessedCallback = useRef(false)

  // Get authentication state from react-oidc-context
  const auth = useAuth()
  const { isAuthenticated, isLoading, error: authError, oidcProfile } = auth

  useEffect((): void => {
    const processCallback = async (): Promise<void> => {
      // Prevent double execution in React Strict Mode
      if (hasProcessedCallback.current) {
        logger.info('Already processed, skipping')
        return
      }
      hasProcessedCallback.current = true

      logger.info('Starting react-oidc-context callback processing', {
        isAuthenticated,
        isLoading,
        hasError: !!authError,
        hasUser: !!oidcProfile,
        url: window.location.href,
      })

      try {
        // Handle error state from react-oidc-context
        if (authError) {
          logger.error(
            'react-oidc-context error detected',
            authError instanceof Error ? authError : undefined,
            authError instanceof Error ? undefined : { authError },
          )
          setError('Authentication failed')
          return
        }

        // Handle successful authentication
        if (isAuthenticated && oidcProfile) {
          logger.info('Authentication successful via react-oidc-context', {
            userId: oidcProfile.profile?.sub,
            email: oidcProfile.profile?.email,
            hasAccessToken: !!oidcProfile.access_token,
            hasIdToken: !!oidcProfile.id_token,
            expired: oidcProfile.expired,
          })

          // Authentication state is automatically updated by unified hook

          logger.info('Navigating to dashboard')
          navigate('/dashboard')
          return
        }

        // If still loading, let react-oidc-context continue processing
        if (isLoading) {
          logger.info('react-oidc-context still processing callback')
          return
        }

        // If not authenticated, loading finished, and no error, something went wrong
        if (!isAuthenticated && !isLoading && !authError) {
          logger.error('Callback completed but user not authenticated')
          setError('Authentication failed - callback did not complete successfully')
        }

      } catch (error) {
        logger.error(
          'Callback processing error',
          error instanceof Error ? error : undefined,
          error instanceof Error ? undefined : { error },
        )
        setError('Authentication failed')
      }
    }

    // Process callback when auth state changes
    processCallback()
  }, [isAuthenticated, isLoading, authError, oidcProfile, navigate])

  // Show loading state while react-oidc-context is processing callback
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Processing Authentication
            </h2>
            <p className="text-gray-600">
              Please wait while we complete your sign-in...
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Show error state if callback processing failed
  if (authError || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="rounded-full bg-red-100 p-3 mx-auto w-16 h-16 flex items-center justify-center mb-4">
              <svg 
                className="w-8 h-8 text-red-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <button
              onClick={() => navigate('/auth/login', { replace: true })}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // This should not be reached due to navigation, but provide fallback
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Complete
          </h2>
          <p className="text-gray-600">
            Redirecting to dashboard...
          </p>
        </div>
      </div>
    </div>
  )
}
